// CookiePolicy.js
import React from 'react';
import {
  ChakraProvider,
  Box,
  Heading,
  Text,
  VStack,
  Button,
  Link,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { ArrowBackIcon } from '@chakra-ui/icons';

const CookiePolicy = () => {
  const tableBg = useColorModeValue('white', 'gray.700');

  return (
    <ChakraProvider>
      <Box
        display={'flex'}
        w={['95%', null, null, null, '50%']}
        mr={['2.5%', null, null, null, '25%']}
        alignItems={'center'}
        justifyContent={'flex-start'}
      >
        <Link as={ReactRouterLink} to={'/'}>
          <Button
            color={'#242424'}
            alignSelf={'flex-end'}
            aria-label="Inicio"
            leftIcon={<ArrowBackIcon />}
            bgColor={'transparent'}
          >
            Volver
          </Button>
        </Link>
      </Box>
      <Box w={['90%', null, null, null, '70%']} mx="auto" mt="4" pb="8">
        <Heading mb="4">Política de Cookies de RouteForecast.app</Heading>
        <Text fontSize="sm" color="gray.600" mb="6">
          Última actualización: 28 de enero de 2026
        </Text>

        <VStack align="start" spacing="6">
          <Text>
            <strong>1. ¿Qué son las cookies?</strong>
            <br />
            Las cookies son pequeños archivos de texto que los sitios web
            almacenan en tu dispositivo cuando los visitas. Se utilizan
            ampliamente para hacer que los sitios web funcionen de manera más
            eficiente, así como para proporcionar información a los propietarios
            del sitio.
          </Text>

          <Text>
            <strong>2. ¿Cómo utilizamos las cookies?</strong>
            <br />
            RouteForecast.app utiliza cookies para mejorar tu experiencia,
            analizar el uso del sitio y mostrar publicidad relevante. Algunas
            cookies son esenciales para el funcionamiento del sitio, mientras
            que otras son opcionales y requieren tu consentimiento.
          </Text>

          <Box w="100%">
            <Text fontWeight="bold" mb="3">
              3. Tipos de cookies que utilizamos
            </Text>

            <Heading size="sm" mt="4" mb="2">
              3.1. Cookies Técnicas (Necesarias)
            </Heading>
            <Text mb="3">
              Estas cookies son esenciales para que el sitio funcione
              correctamente. No requieren consentimiento según la legislación
              vigente.
            </Text>
            <TableContainer bg={tableBg} borderRadius="md" mb="4">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Cookie</Th>
                    <Th>Propósito</Th>
                    <Th>Duración</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      routeforecast_session
                    </Td>
                    <Td>Mantener la sesión del usuario</Td>
                    <Td>Sesión</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      cookie_consent
                    </Td>
                    <Td>Guardar preferencias de cookies</Td>
                    <Td>1 año</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>

            <Heading size="sm" mt="6" mb="2">
              3.2. Cookies Analíticas (Google Analytics)
            </Heading>
            <Text mb="3">
              Utilizamos Google Analytics para entender cómo los visitantes
              interactúan con nuestro sitio. Estas cookies recopilan información
              de forma anónima. Requieren tu consentimiento.
            </Text>
            <TableContainer bg={tableBg} borderRadius="md" mb="4">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Cookie</Th>
                    <Th>Propósito</Th>
                    <Th>Duración</Th>
                    <Th>Tercero</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      _ga
                    </Td>
                    <Td>Distinguir usuarios únicos</Td>
                    <Td>2 años</Td>
                    <Td>Google</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      _ga_*
                    </Td>
                    <Td>Mantener estado de sesión</Td>
                    <Td>2 años</Td>
                    <Td>Google</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      _gid
                    </Td>
                    <Td>Distinguir usuarios únicos</Td>
                    <Td>24 horas</Td>
                    <Td>Google</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      _gat
                    </Td>
                    <Td>Limitar tasa de solicitudes</Td>
                    <Td>1 minuto</Td>
                    <Td>Google</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
            <Text fontSize="sm" color="gray.600" mb="4">
              Información sobre privacidad de Google Analytics:{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://policies.google.com/privacy
              </a>
            </Text>

            <Heading size="sm" mt="6" mb="2">
              3.3. Cookies Publicitarias (Google AdSense)
            </Heading>
            <Text mb="3">
              Utilizamos Google AdSense para mostrar anuncios. Estas cookies
              permiten mostrar publicidad relevante basada en tus intereses.
              Requieren tu consentimiento.
            </Text>
            <TableContainer bg={tableBg} borderRadius="md" mb="4">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Cookie</Th>
                    <Th>Propósito</Th>
                    <Th>Duración</Th>
                    <Th>Tercero</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      __gads
                    </Td>
                    <Td>Mostrar anuncios personalizados</Td>
                    <Td>13 meses</Td>
                    <Td>Google</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      __gpi
                    </Td>
                    <Td>Información sobre anuncios</Td>
                    <Td>13 meses</Td>
                    <Td>Google</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      DSID
                    </Td>
                    <Td>Identificar dispositivo del usuario</Td>
                    <Td>2 semanas</Td>
                    <Td>Google</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      IDE
                    </Td>
                    <Td>Remarketing, personalización</Td>
                    <Td>13 meses</Td>
                    <Td>Google</Td>
                  </Tr>
                  <Tr>
                    <Td fontFamily="mono" fontSize="sm">
                      test_cookie
                    </Td>
                    <Td>Comprobar si el navegador acepta cookies</Td>
                    <Td>15 minutos</Td>
                    <Td>Google</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
            <Text fontSize="sm" color="gray.600" mb="4">
              Información sobre publicidad de Google:{' '}
              <a
                href="https://policies.google.com/technologies/ads"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://policies.google.com/technologies/ads
              </a>
              <br />
              Configurar preferencias de anuncios:{' '}
              <a
                href="https://adssettings.google.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://adssettings.google.com/
              </a>
            </Text>
          </Box>

          <Text>
            <strong>4. Cookies de Terceros</strong>
            <br />
            Algunos servicios de terceros pueden establecer sus propias cookies:
            <br />- <strong>Google (Analytics y AdSense):</strong> Como se
            describe arriba
            <br />- <strong>Redes sociales:</strong> Si compartes contenido en
            redes sociales
            <br />
            <br />
            No tenemos control sobre estas cookies de terceros. Consulta las
            políticas de privacidad de los terceros para más información.
          </Text>

          <Text>
            <strong>5. ¿Cómo gestionar las cookies?</strong>
            <br />
            Tienes varias opciones para gestionar las cookies:
            <br />
            <br />
            <strong>5.1. Panel de Consentimiento de Cookies:</strong>
            <br />
            Al visitar nuestro sitio por primera vez, aparecerá un banner donde
            puedes aceptar o rechazar cookies no esenciales. Puedes cambiar tus
            preferencias en cualquier momento haciendo clic en el enlace
            "Configuración de Cookies" en el pie de página.
            <br />
            <br />
            <strong>5.2. Configuración del Navegador:</strong>
            <br />
            Puedes configurar tu navegador para bloquear o eliminar cookies:
            <br />- <strong>Chrome:</strong>{' '}
            <a
              href="https://support.google.com/chrome/answer/95647"
              target="_blank"
              rel="noopener noreferrer"
            >
              Configurar cookies en Chrome
            </a>
            <br />- <strong>Firefox:</strong>{' '}
            <a
              href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias"
              target="_blank"
              rel="noopener noreferrer"
            >
              Configurar cookies en Firefox
            </a>
            <br />- <strong>Safari:</strong>{' '}
            <a
              href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
              target="_blank"
              rel="noopener noreferrer"
            >
              Configurar cookies en Safari
            </a>
            <br />- <strong>Edge:</strong>{' '}
            <a
              href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
              target="_blank"
              rel="noopener noreferrer"
            >
              Configurar cookies en Edge
            </a>
            <br />
            <br />
            <strong>5.3. Herramientas de Terceros:</strong>
            <br />- <strong>Google Ads Settings:</strong>{' '}
            <a
              href="https://adssettings.google.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Configurar anuncios de Google
            </a>
            <br />- <strong>Your Online Choices:</strong>{' '}
            <a
              href="http://www.youronlinechoices.com/es/"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.youronlinechoices.com/es/
            </a>
          </Text>

          <Text>
            <strong>6. Consecuencias de Deshabilitar Cookies</strong>
            <br />
            Si deshabilitas las cookies:
            <br />- <strong>Cookies técnicas:</strong> El sitio puede no
            funcionar correctamente
            <br />- <strong>Cookies analíticas:</strong> No podremos mejorar el
            sitio basándonos en tu uso
            <br />- <strong>Cookies publicitarias:</strong> Seguirás viendo
            anuncios, pero menos relevantes
          </Text>

          <Text>
            <strong>7. Base Legal (RGPD)</strong>
            <br />- <strong>Cookies técnicas:</strong> Interés legítimo
            (necesarias para el servicio)
            <br />- <strong>Cookies analíticas:</strong> Consentimiento del
            usuario
            <br />- <strong>Cookies publicitarias:</strong> Consentimiento del
            usuario
            <br />
            <br />
            Puedes retirar tu consentimiento en cualquier momento sin que afecte
            a la legalidad del tratamiento basado en el consentimiento previo.
          </Text>

          <Text>
            <strong>8. Transferencias Internacionales</strong>
            <br />
            Google LLC (proveedor de Analytics y AdSense) puede transferir datos
            fuera del Espacio Económico Europeo. Google cumple con el Marco de
            Privacidad de Datos UE-EE.UU. (EU-U.S. Data Privacy Framework).
          </Text>

          <Text>
            <strong>9. Actualizaciones de esta Política</strong>
            <br />
            Podemos actualizar esta Política de Cookies periódicamente. La fecha
            de la última actualización se muestra al inicio del documento. Te
            recomendamos revisar esta página regularmente.
          </Text>

          <Text>
            <strong>10. Más Información y Contacto</strong>
            <br />
            Para más información sobre cómo tratamos tus datos, consulta nuestra{' '}
            <Link
              as={ReactRouterLink}
              to="/privacidad"
              color="blue.500"
              textDecoration="underline"
            >
              Política de Privacidad
            </Link>
            .
            <br />
            <br />
            Para preguntas sobre cookies:
            <br />
            <strong>Responsable:</strong> Pablo Osés Andía
            <br />
            <strong>Domicilio:</strong> Calle Rosario Romero 8 1B, 28029 Madrid,
            España
            <br />
            <strong>Email:</strong>{' '}
            <a href="mailto:pablo98oses@gmail.com">pablo98oses@gmail.com</a>
          </Text>
        </VStack>

        <Box mt="8" p="4" bg="blue.50" borderRadius="md">
          <Text fontSize="sm">
            <strong>💡 Consejo:</strong> Puedes gestionar tus preferencias de
            cookies en cualquier momento desde el banner de cookies o desde la
            configuración de tu navegador. Respetamos tu privacidad y tu derecho
            a controlar qué datos se recopilan.
          </Text>
        </Box>
      </Box>
    </ChakraProvider>
  );
};

export default CookiePolicy;
